// apps/app/src/routes/Courses/Lesson/index.tsx

import { Button } from "@/components/ui/button";
import { Text } from "@/components/ui/text";
import { Layout } from "@components";
import { DataGuard } from "@components/DataGuard";
import LayoutHeader from "@components/LayoutHeader";
import { A, useNavigate, useParams } from "@solidjs/router";
import { ArrowLeft, ArrowRight } from "lucide-solid";
import { Box, Center, Grid } from "styled-system/jsx";

import Panel from "@components/Panel";
import { Match, Switch, createEffect } from "solid-js";
import { useService } from "solid-services";
import { useCourseOverview } from "../useCourseOverview";
import QuizContent from "./QuizContent";
import TextContent from "./TextContent";
import VideoContent from "./VideoContent";
import { useLessonState } from "./state";

// Import the generic Comments feature and the course adapter
import Comments from "@features/Comments";
import { courseCommentsAdapter } from "./courseCommentsAdapter";

export default function Lesson() {
  const params = useParams();
  const navigate = useNavigate();

  const {
    lessonQuery,
    currentProgress, // Use the new derived state here
    setLessonSlug,
    markAsComplete,
    isUpdatingProgress,
    nextLessonSlug,
    requirementsMet
  } = useService(useLessonState)()

  const { activeCourseslug, activeLessonOverviewQuery } = useService(useCourseOverview)()

  createEffect(() => {
    if (params.lessonSlug) {
      setLessonSlug(params.lessonSlug);
    }
  });

  const handleNext = async () => {
    if (currentProgress()?.status === 'completed') {
      navigate(`/courses/${activeCourseslug()}`)
      return
    }

    // Mark complete in background before navigating
    if (currentProgress()?.status !== 'completed') {
      await markAsComplete();
    }
    const next = nextLessonSlug();
    if (next) navigate(`/courses/${activeCourseslug()}/${next}`);
  };

  return (
    // <Grid gap='0'>
    <DataGuard
      query={lessonQuery}
    >
      {() => {
        const lesson = lessonQuery.data;

        if (!lesson) {
          return <Center p="10"><Text>Lesson not found.</Text></Center>;
        }

        // Derive UI states from our memoized progress
        const isCompleted = currentProgress()?.status === 'completed';
        const nextSlug = nextLessonSlug();

        return (
          <>
            <LayoutHeader
              title={lesson.title ?? ''}
              subTitle={lesson.section?.course?.title ?? ''}
              rightContent={
                <A href={`/courses/${activeLessonOverviewQuery.data?.slug}`}>
                  <Button size='xs' variant='outline'>
                    <ArrowLeft size={16} />
                    Back to Course
                  </Button>
                </A>
              }
            />

            <Panel height={{
              base: "calc(var(--vh) * 100 - 102px)",
              mdDown: "calc(var(--vh) * 100 - 162px)"
            }} overflowY="auto">
              <Switch fallback={<Center p="10">Unsupported lesson type: {lesson.type}</Center>}>
                <Match when={lesson.type === 'video'}>
                  <VideoContent />
                </Match>
                <Match when={lesson.type === 'text'}>
                  <TextContent />
                </Match>
                <Match when={lesson.type === 'quiz'}>
                  <QuizContent />
                </Match>
              </Switch>

              <Grid gap='3' width="full" maxW="4xl" py="3" mx="auto">

                <Box display="flex" justifyContent="flex-end">
                  <Button
                    colorPalette="jade"
                    disabled={
                      lessonQuery.data?.type !== 'text' && // Text is always allowed
                      !requirementsMet() &&                // Otherwise check if requirements are met
                      currentProgress()?.status !== 'completed' // Unless already finished previously
                    }
                    loading={isUpdatingProgress}
                    onClick={handleNext}
                  >
                    {nextLessonSlug() ? "Next Lesson" : "Finish Course"}
                    <ArrowRight size={18} style={{ "margin-left": "8px" }} />
                  </Button>
                </Box>

                <Comments
                  adapter={courseCommentsAdapter}
                  entityId={lesson.id}
                />
              </Grid>

            </Panel>
          </>
        );
      }}
    </DataGuard>
    // </Grid>
  );
}
